import { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Hash } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn } from '@/components/DataTable/types'
import { fetchDialPrefixes, type DialPrefixRow } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A11.11 — Raqam prefikslari. `/admin/countries` ni dial-kod bo'yicha guruhlaydi;
 * ulashilgan prefiks (+7 = RU/KZ) belgilanadi — coarse-block xavfini ko'rsatadi.
 * Alohida endpoint yo'q (klient-agregat).
 */
export function DialPrefixes() {
  const t = useT()

  const columns = useMemo<AdminColumn<DialPrefixRow>[]>(
    () => [
      {
        key: 'dial_code',
        header: t.col.dialCode,
        type: 'text',
        width: 100,
        render: (r) => <span className="font-mono tabular text-primary">{r.dial_code}</span>,
      },
      {
        key: 'countries',
        header: t.nav.geo,
        type: 'text',
        minWidth: 260,
        sortable: false,
        render: (r) => (
          <span className="flex flex-wrap gap-x-2 gap-y-0.5">
            {r.countries.map((c) => (
              <Link key={c.code} to={`/geo/${c.code}`} className="text-accent hover:underline">
                {c.flag ? `${c.flag} ` : ''}
                {c.name}
              </Link>
            ))}
          </span>
        ),
      },
      {
        key: 'shared',
        header: t.geo.shared,
        type: 'bool',
        width: 110,
        accessor: (r) => r.shared,
      },
      {
        key: 'users',
        header: t.col.registrations,
        type: 'number',
        width: 120,
        align: 'right',
        render: (r) => <span className="font-mono tabular">{r.users.toLocaleString()}</span>,
      },
    ],
    [t],
  )

  const fetcher = useCallback(fetchDialPrefixes, [])

  return (
    <>
      <PageHeader title={t.geo.dialPrefixes} screenId="A11.11" description={t.geo.dialPrefixesNote} />
      <DataTable<DialPrefixRow>
        screenId="A11.11"
        permission={PERMISSIONS.COUNTRIES_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => r.dial_code}
        exportable
        emptyState={{ icon: <Hash size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
